'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Wrench, User, ShieldCheck, UserPlus, HardHat, Smartphone, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import LegalFooter from '@/components/LegalFooter';
import { useLanguage } from '@/context/LanguageContext';
import { SUPPORTED_LANGUAGES } from '@/lib/i18n';
import { createClient } from '@/lib/supabase/client';
import {
  isDailyAuthValid,
  saveDailyAuthSession,
  sendOtpEmail,
  extractRefCodeFromUrl,
  createPendingReferral,
  getGlobalSettings,
} from '@/lib/workshopService';

type CustomerFormData = { identifier: string; pin: string };
type AdminFormData = { identifier: string; password: string };
type StaffFormData = { identifier: string; staffPin: string };
type SignUpFormData = {
  fullName: string; phone: string; email: string;
  vehicleNumber: string; pin: string; confirmPin: string;
};

// ─── Admin Credentials (localStorage fallback for setup) ─────────────────────
const ADMIN_CREDS_KEY = 'cw_admin_credentials';
function getAdminCredentials(): { identifier: string; password: string } | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(ADMIN_CREDS_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch { return null; }
}

// ─── Device trust helpers ─────────────────────────────────────────────────────
const TRUST_KEY = 'cw_trusted_device_expiry';
function isDeviceTrusted(): boolean {
  if (typeof window === 'undefined') return false;
  const expiry = localStorage.getItem(TRUST_KEY);
  if (!expiry) return false;
  return Date.now() < parseInt(expiry, 10);
}
function trustDevice() {
  const expiry = Date.now() + 30 * 24 * 60 * 60 * 1000;
  localStorage.setItem(TRUST_KEY, String(expiry));
}
function clearDeviceTrust() { localStorage.removeItem(TRUST_KEY); }

function generateOtp(): string {
  return String(Math.floor(100000 + Math.random() * 900000));
}

// ─── Staff credentials ────────────────────────────────────────────────────────
function getStaffCredentials(): Array<{ identifier: string; name: string; pin: string }> {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem('cw_staff_credentials');
    if (!stored) {
      const defaults = [{ identifier: '9999999999', name: 'मैकेनिक', pin: '2233' }];
      localStorage.setItem('cw_staff_credentials', JSON.stringify(defaults));
      return defaults;
    }
    return JSON.parse(stored);
  } catch { return []; }
}

export default function LoginPageClient() {
  const router = useRouter();
  const { language, setLanguage, t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'customer' | 'staff' | 'admin' | 'signup'>('customer');
  const [showPin, setShowPin] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [showStaffPin, setShowStaffPin] = useState(false);
  const [showSignUpPin, setShowSignUpPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [deviceTrusted, setDeviceTrusted] = useState(false);
  const [trustCheckbox, setTrustCheckbox] = useState(false);
  const [pendingRefCode, setPendingRefCode] = useState<string | null>(null);

  // Admin OTP flow
  const [adminStep, setAdminStep] = useState<'credentials' | 'otp'>('credentials');
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [otpError, setOtpError] = useState('');
  const [adminIdentifierForOtp, setAdminIdentifierForOtp] = useState('');
  const [adminUserIdForSession, setAdminUserIdForSession] = useState('');
  const [dailyAuthValid, setDailyAuthValid] = useState(false);

  useEffect(() => {
    setDeviceTrusted(isDeviceTrusted());
    const refCode = extractRefCodeFromUrl();
    if (refCode) { setPendingRefCode(refCode); setActiveTab('signup'); }

    // Check daily auth via Supabase
    const checkDailyAuth = async () => {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const valid = await isDailyAuthValid(user.id);
        setDailyAuthValid(valid);
        if (valid) setAdminUserIdForSession(user.id);
      }
    };
    checkDailyAuth();
  }, []);

  const customerForm = useForm<CustomerFormData>({ defaultValues: { identifier: '', pin: '' } });
  const adminForm = useForm<AdminFormData>({ defaultValues: { identifier: '', password: '' } });
  const staffForm = useForm<StaffFormData>({ defaultValues: { identifier: '', staffPin: '' } });
  const signUpForm = useForm<SignUpFormData>({ defaultValues: { fullName: '', phone: '', email: '', vehicleNumber: '', pin: '', confirmPin: '' } });

  // ─── Customer Login ───────────────────────────────────────────────────────
  const onCustomerSubmit = async (data: CustomerFormData) => {
    setIsLoading(true);
    try {
      const identifier = data.identifier.trim();
      const identifierLower = identifier.toLowerCase();
      const isEmail = identifierLower.includes('@');

      // ── STEP 1: Try Supabase auth (with timeout guard) ──────────────────
      let supabaseSuccess = false;
      try {
        const supabase = createClient();
        let email = isEmail ? identifierLower : '';
        if (!isEmail) {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('email')
            .eq('phone', identifier)
            .single();
          if (profile) email = profile.email;
        }

        if (email) {
          const { data: authData, error } = await supabase.auth.signInWithPassword({
            email,
            password: data.pin,
          });

          if (!error && authData.user) {
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('*')
              .eq('id', authData.user.id)
              .single();

            if (profile && profile.role === 'customer') {
              localStorage.setItem('cw_role', 'customer');
              localStorage.setItem('cw_user', JSON.stringify({
                id: profile.id,
                phone: profile.phone,
                email: profile.email,
                name: profile.full_name,
                vehicleNumber: profile.vehicle_number,
              }));
              toast.success(`लॉगिन सफल! वेलकम ${profile.full_name} जी 🙏`);
              router.push('/customer-dashboard');
              supabaseSuccess = true;
              return;
            }
          }
        }
      } catch (supabaseErr) {
        console.error('Supabase sign-in error (falling back to local):', supabaseErr);
      }

      if (supabaseSuccess) return;

      // ── STEP 2: localStorage registered customers fallback ───────────────
      const stored = localStorage.getItem('cw_registered_customers');
      const customers = stored ? JSON.parse(stored) : [];
      const matchedCustomer = isEmail
        ? customers.find((c: { email: string; pin: string }) => c.email.toLowerCase() === identifierLower && c.pin === data.pin)
        : customers.find((c: { phone: string; pin: string }) => c.phone === identifier && c.pin === data.pin);

      if (matchedCustomer) {
        localStorage.setItem('cw_role', 'customer');
        localStorage.setItem('cw_user', JSON.stringify({
          id: matchedCustomer.id || '',
          phone: matchedCustomer.phone,
          email: matchedCustomer.email,
          name: matchedCustomer.name,
          vehicleNumber: matchedCustomer.vehicleNumber,
        }));
        toast.success(`लॉगिन सफल! वेलकम ${matchedCustomer.name} जी 🙏`);
        router.push('/customer-dashboard');
        return;
      }

      // ── STEP 3: workshop_users fallback (auto-register guest) ────────────
      const workshopUsers: Array<{
        id: string; phone: string; email?: string;
        walletBalance: number; referralCode: string; referredBy: string | null; pin?: string;
      }> = JSON.parse(localStorage.getItem('workshop_users') || '[]');
      let existingUser = workshopUsers.find(u => u.phone === identifier || u.email === identifierLower);

      if (!existingUser) {
        // Auto-register as guest user so they are never blocked
        existingUser = {
          id: 'usr_' + Date.now(),
          phone: isEmail ? '' : identifier,
          email: isEmail ? identifierLower : '',
          walletBalance: 0,
          referralCode: 'CW' + Math.floor(1000 + Math.random() * 9000),
          referredBy: pendingRefCode || null,
          pin: data.pin,
        };
        workshopUsers.push(existingUser);
        localStorage.setItem('workshop_users', JSON.stringify(workshopUsers));

        // Also add to cw_registered_customers for future logins
        const newCustomer = {
          id: existingUser.id,
          phone: existingUser.phone,
          email: existingUser.email || '',
          name: isEmail ? identifierLower.split('@')[0] : `ग्राहक ${identifier.slice(-4)}`,
          vehicleNumber: '',
          pin: data.pin,
        };
        const existingList = JSON.parse(localStorage.getItem('cw_registered_customers') || '[]');
        localStorage.setItem('cw_registered_customers', JSON.stringify([...existingList, newCustomer]));

        localStorage.setItem('cw_role', 'customer');
        localStorage.setItem('cw_user', JSON.stringify({
          id: existingUser.id,
          phone: existingUser.phone,
          email: existingUser.email || '',
          name: newCustomer.name,
          vehicleNumber: '',
        }));
        localStorage.setItem('workshop_current_user', JSON.stringify(existingUser));
        toast.success(`🎉 स्वागत है! Guest account बना दिया गया है।`);
        router.push('/customer-dashboard');
        return;
      }

      // Existing workshop_users entry — verify PIN if set
      if (existingUser.pin && existingUser.pin !== data.pin) {
        toast.error('गलत PIN — कृपया दोबारा कोशिश करें');
        customerForm.setError('pin', { message: 'गलत PIN' });
        setIsLoading(false);
        return;
      }

      localStorage.setItem('workshop_current_user', JSON.stringify(existingUser));
      localStorage.setItem('cw_role', 'customer');
      localStorage.setItem('cw_user', JSON.stringify({
        id: existingUser.id,
        phone: existingUser.phone,
        email: existingUser.email || '',
        name: isEmail ? identifierLower.split('@')[0] : `ग्राहक ${identifier.slice(-4)}`,
        vehicleNumber: '',
      }));
      toast.success(`लॉगिन सफल! वेलकम वापस 🙏`);
      router.push('/customer-dashboard');
    } catch (err) {
      console.error('Sign-in error handled:', err);
      toast.error('साइन-इन में त्रुटि, कृपया पुनः प्रयास करें।');
    }
    setIsLoading(false);
  };

  // ─── Staff Login ──────────────────────────────────────────────────────────
  const onStaffSubmit = async (data: StaffFormData) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 800));
    const staffList = getStaffCredentials();
    const identifier = data.identifier.trim().toLowerCase();
    const matched = staffList.find(s =>
      (s.identifier === data.identifier.trim() || s.identifier.toLowerCase() === identifier) &&
      s.pin === data.staffPin
    );
    if (matched) {
      localStorage.setItem('cw_role', 'staff');
      localStorage.setItem('cw_user', JSON.stringify({ name: matched.name, role: 'staff' }));
      toast.success(`👨‍🔧 वेलकम ${matched.name} जी! Staff पैनल में आपका स्वागत है।`);
      router.push('/staff-dashboard');
    } else {
      toast.error('गलत Mobile/Email या Staff PIN — मालिक से संपर्क करें');
      staffForm.setError('staffPin', { message: 'गलत credentials' });
    }
    setIsLoading(false);
  };

  // ─── Admin Step 1: Credentials ────────────────────────────────────────────
  const onAdminCredentialsSubmit = async (data: AdminFormData) => {
    setIsLoading(true);
    try {
      if (data.password.length < 6) {
        adminForm.setError('password', { message: 'पासवर्ड कम से कम 6 अंकों का होना चाहिए।' });
        setIsLoading(false);
        return;
      }

      // ─── SECURITY: Hardcoded authorized admin email check ─────────────────
      const AUTHORIZED_ADMIN_EMAIL = 'ramhitg1703@gmail.com';
      const inputId = data.identifier.trim().toLowerCase();
      if (inputId !== AUTHORIZED_ADMIN_EMAIL) {
        toast.error('Access Denied: Unauthorized Admin');
        adminForm.setError('identifier', { message: 'Access Denied: Unauthorized Admin' });
        setIsLoading(false);
        return;
      }
      // ─────────────────────────────────────────────────────────────────────

      const creds = getAdminCredentials();
      if (!creds) {
        toast.error('Admin पासवर्ड सेट नहीं है। पहले Setup करें।');
        setIsLoading(false);
        router.push('/set-admin-password');
        return;
      }

      const storedId = creds.identifier.trim().toLowerCase();
      if (inputId !== storedId || data.password !== creds.password) {
        toast.error('गलत Mobile/Email या पासवर्ड — कृपया सही जानकारी डालें');
        adminForm.setError('password', { message: 'गलत credentials' });
        setIsLoading(false);
        return;
      }

      // Try Supabase sign-in for admin
      const supabase = createClient();
      const adminEmail = AUTHORIZED_ADMIN_EMAIL;

      let supabaseUserId = '';
      try {
        const { data: authData } = await supabase.auth.signInWithPassword({
          email: adminEmail,
          password: data.password,
        });
        if (authData?.user) supabaseUserId = authData.user.id;
      } catch { /* use localStorage fallback */ }

      // Check daily auth
      let dailyValid = false;
      if (supabaseUserId) {
        dailyValid = await isDailyAuthValid(supabaseUserId);
      } else {
        // Fallback: localStorage daily auth
        const stored = localStorage.getItem('cw_daily_auth');
        if (stored) {
          const { date, expiry } = JSON.parse(stored);
          const today = new Date();
          const todayStr = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
          dailyValid = date === todayStr && Date.now() < expiry;
        }
      }

      if (dailyValid) {
        // Same day — skip OTP
        if (supabaseUserId) setAdminUserIdForSession(supabaseUserId);
        completeAdminLogin(adminEmail, supabaseUserId, true);
      } else {
        // First login of day — send OTP to email
        const otp = generateOtp();
        setGeneratedOtp(otp);
        setAdminIdentifierForOtp(adminEmail);
        if (supabaseUserId) setAdminUserIdForSession(supabaseUserId);
        setAdminStep('otp');

        const result = await sendOtpEmail(adminEmail, otp, 'Admin');
        if (result?.demo) {
          // Email delivery unavailable — show OTP directly on screen
          toast.success(`🔐 आपका OTP: ${result.otp || otp}`, { duration: 30000 });
        } else {
          // Email sent successfully — also show OTP as backup
          toast.success(`📧 OTP ramhitg1703@gmail.com पर भेजा गया। OTP: ${otp}`, { duration: 30000 });
        }
      }
    } catch {
      toast.error('Login में समस्या — दोबारा कोशिश करें');
    }
    setIsLoading(false);
  };

  // ─── Admin Step 2: OTP Verify ─────────────────────────────────────────────
  const handleOtpVerify = async () => {
    if (otpInput.trim() !== generatedOtp) {
      setOtpError('गलत OTP — कृपया सही OTP डालें');
      return;
    }
    setOtpError('');
    setIsLoading(true);

    // Save daily auth session
    if (adminUserIdForSession) {
      await saveDailyAuthSession(adminUserIdForSession);
    } else {
      // Fallback localStorage
      const now = new Date();
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      const today = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
      localStorage.setItem('cw_daily_auth', JSON.stringify({ date: today, expiry: endOfDay.getTime() }));
    }
    setDailyAuthValid(true);

    const creds = getAdminCredentials();
    completeAdminLogin(creds?.identifier || adminIdentifierForOtp, adminUserIdForSession, false);
    setIsLoading(false);
  };

  const completeAdminLogin = (identifier: string, userId: string, wasDailyAuth: boolean) => {
    if (trustCheckbox && !deviceTrusted) trustDevice();
    localStorage.setItem('cw_role', 'admin');
    localStorage.setItem('cw_user', JSON.stringify({ id: userId, name: 'रामकिशन चौरासिया', role: 'admin' }));
    const msg = wasDailyAuth
      ? '⚡ Daily Auth — सिर्फ पासवर्ड से Login! मालिक पैनल में आपका स्वागत है! 🔧' :'✅ OTP Verified! मालिक पैनल में आपका स्वागत है! 🔧';
    toast.success(msg);
    router.push('/admin-dashboard');
  };

  // ─── Sign Up ──────────────────────────────────────────────────────────────
  const onSignUpSubmit = async (data: SignUpFormData) => {
    setIsLoading(true);
    try {
      if (data.pin !== data.confirmPin) {
        signUpForm.setError('confirmPin', { message: 'PIN मेल नहीं खाता — दोबारा डालें' });
        setIsLoading(false);
        return;
      }

      // ── STEP 1: Save to localStorage FIRST (never block the user) ────────
      const localId = 'usr_' + Date.now();
      const newCustomer = {
        id: localId,
        phone: data.phone.trim(),
        email: data.email.trim().toLowerCase(),
        name: data.fullName.trim(),
        vehicleNumber: data.vehicleNumber.trim().toUpperCase(),
        pin: data.pin,
      };
      const existingList = JSON.parse(localStorage.getItem('cw_registered_customers') || '[]');
      // Check for duplicate phone/email in localStorage
      const dupLocal = existingList.find(
        (c: { phone: string; email: string }) =>
          c.phone === newCustomer.phone || c.email === newCustomer.email
      );
      if (!dupLocal) {
        localStorage.setItem('cw_registered_customers', JSON.stringify([...existingList, newCustomer]));
      }

      // Also save to workshop_users for wallet/referral tracking
      const workshopUsers: Array<{
        id: string; phone: string; email?: string;
        walletBalance: number; referralCode: string; referredBy: string | null; pin?: string;
      }> = JSON.parse(localStorage.getItem('workshop_users') || '[]');
      const dupWorkshop = workshopUsers.find(u => u.phone === newCustomer.phone || u.email === newCustomer.email);
      if (!dupWorkshop) {
        const workshopEntry = {
          id: localId,
          phone: newCustomer.phone,
          email: newCustomer.email,
          walletBalance: 0,
          referralCode: 'CW' + Math.floor(1000 + Math.random() * 9000),
          referredBy: pendingRefCode || null,
          pin: data.pin,
        };
        workshopUsers.push(workshopEntry);
        localStorage.setItem('workshop_users', JSON.stringify(workshopUsers));
        localStorage.setItem('workshop_current_user', JSON.stringify(workshopEntry));
      }

      // ── STEP 2: Handle referral bonus in localStorage immediately ────────
      if (pendingRefCode) {
        try {
          const allUsers: Array<{
            id: string; phone: string; referralCode: string; walletBalance: number;
          }> = JSON.parse(localStorage.getItem('workshop_users') || '[]');
          const referrer = allUsers.find(u => u.referralCode === pendingRefCode);
          if (referrer && referrer.phone !== newCustomer.phone) {
            // Credit referral reward to referrer's local wallet
            const globalSettings = await getGlobalSettings().catch(() => ({ referralRewardAmount: 50, walletCashbackPercent: 2 }));
            const rewardAmount = globalSettings.referralRewardAmount;
            referrer.walletBalance = (referrer.walletBalance || 0) + rewardAmount;
            const updatedUsers = allUsers.map(u => u.phone === referrer.phone ? referrer : u);
            localStorage.setItem('workshop_users', JSON.stringify(updatedUsers));
            // Also update cw_wallet_ key
            const walletKey = `cw_wallet_${referrer.phone}`;
            const current = Number(localStorage.getItem(walletKey) || '0');
            localStorage.setItem(walletKey, String(current + rewardAmount));
          }
        } catch { /* silent — referral is best-effort */ }
      }

      // ── STEP 3: Set session immediately so user is logged in ─────────────
      localStorage.setItem('cw_role', 'customer');
      localStorage.setItem('cw_user', JSON.stringify({
        id: localId,
        phone: newCustomer.phone,
        email: newCustomer.email,
        name: newCustomer.name,
        vehicleNumber: newCustomer.vehicleNumber,
      }));

      // ── STEP 4: Try Supabase in background (non-blocking) ────────────────
      let supabaseUserId = localId;
      try {
        const supabase = createClient();
        const { data: authData, error: signUpError } = await supabase.auth.signUp({
          email: data.email.trim().toLowerCase(),
          password: data.pin,
          options: {
            data: {
              full_name: data.fullName.trim(),
              phone: data.phone.trim(),
              vehicle_number: data.vehicleNumber.trim().toUpperCase(),
              role: 'customer',
              pin: data.pin,
            },
          },
        });

        if (signUpError) {
          if (signUpError.message.includes('already registered')) {
            // User exists in Supabase — try to sign in instead
            const { data: signInData } = await supabase.auth.signInWithPassword({
              email: data.email.trim().toLowerCase(),
              password: data.pin,
            });
            if (signInData?.user) supabaseUserId = signInData.user.id;
          }
          // Other errors: silently continue with localStorage session
        } else if (authData.user) {
          supabaseUserId = authData.user.id;

          // Update localStorage with real Supabase ID
          const updatedUser = { ...newCustomer, id: supabaseUserId };
          const list = JSON.parse(localStorage.getItem('cw_registered_customers') || '[]');
          const updatedList = list.map((c: { phone: string }) =>
            c.phone === newCustomer.phone ? updatedUser : c
          );
          localStorage.setItem('cw_registered_customers', JSON.stringify(updatedList));
          localStorage.setItem('cw_user', JSON.stringify({
            id: supabaseUserId,
            phone: newCustomer.phone,
            email: newCustomer.email,
            name: newCustomer.name,
            vehicleNumber: newCustomer.vehicleNumber,
          }));

          // Handle Supabase referral — wait for trigger
          if (pendingRefCode && supabaseUserId) {
            try {
              await new Promise((r) => setTimeout(r, 1500));
              const globalSettings = await getGlobalSettings();
              const suffix = pendingRefCode.replace('REF', '');
              const { data: referrerProfile } = await supabase
                .from('user_profiles')
                .select('id, phone, wallet_balance')
                .ilike('phone', `%${suffix}`)
                .single();

              if (referrerProfile && referrerProfile.id !== supabaseUserId) {
                // Credit referrer wallet in Supabase
                const newBalance = Number(referrerProfile.wallet_balance || 0) + globalSettings.referralRewardAmount;
                await supabase
                  .from('user_profiles')
                  .update({ wallet_balance: newBalance })
                  .eq('id', referrerProfile.id);

                // Insert referral record
                await createPendingReferral(
                  referrerProfile.id,
                  referrerProfile.phone,
                  supabaseUserId,
                  data.phone.trim(),
                  globalSettings.referralRewardAmount
                );

                // Notify referrer
                await supabase.from('notifications').insert({
                  user_id: referrerProfile.id,
                  message: `🎉 बधाई हो! आपके रेफरल से ${data.fullName.trim()} ने रजिस्टर किया। ₹${globalSettings.referralRewardAmount} आपके वॉलेट में क्रेडिट हो गए!`,
                  is_read: false,
                });
              }
            } catch { /* silent — referral is best-effort */ }
          }
        }
      } catch (supabaseErr) {
        console.error('Supabase sign-up error (user saved locally):', supabaseErr);
        // User is already saved in localStorage — continue normally
      }

      toast.success(`🎉 रजिस्ट्रेशन सफल! वेलकम ${newCustomer.name} जी`);
      router.push('/customer-dashboard');
    } catch (err) {
      console.error('Sign-up error handled:', err);
      toast.error('रजिस्ट्रेशन में समस्या — दोबारा कोशिश करें');
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--background)' }}>
      {/* Top Brand Bar */}
      <div className="w-full py-4 px-6 flex items-center justify-between" style={{ backgroundColor: 'var(--primary)' }}>
        <div className="flex items-center gap-3">
          <AppLogo size={36} />
          <div>
            <div className="text-white font-bold text-lg leading-tight">{t.appName}</div>
            <div className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{t.appTagline} · {t.appCity}</div>
          </div>
        </div>
        <div className="flex items-center rounded-full overflow-hidden border" style={{ borderColor: 'rgba(255,255,255,0.2)', backgroundColor: 'rgba(255,255,255,0.08)' }}>
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold transition-all duration-150"
              style={{
                backgroundColor: language === lang.code ? 'rgba(249,115,22,0.85)' : 'transparent',
                color: language === lang.code ? '#fff' : 'rgba(255,255,255,0.65)',
                borderRadius: '9999px',
              }}
            >
              <span>{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Hero Banner */}
      <div className="w-full px-5 py-5" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)' }}>
        <div className="max-w-md mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-3" style={{ backgroundColor: 'rgba(249,115,22,0.2)', color: '#fb923c' }}>
            <Wrench size={12} />
            {t.onlineBookingDiscount}
          </div>
          <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.75)' }}>{t.walletBonus}</p>
        </div>
      </div>

      {/* Main Card */}
      <div className="flex-1 flex items-start justify-center px-4 py-6">
        <div className="w-full max-w-sm">
          <div className="card-shadow-md rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--card)' }}>
            {/* Tabs */}
            <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
              {[
                { key: 'customer', icon: <User size={14} />, label: '👤 ग्राहक', color: 'var(--accent)' },
                { key: 'signup', icon: <UserPlus size={14} />, label: t.signUp, color: '#16a34a' },
                { key: 'staff', icon: <HardHat size={14} />, label: '👨‍🔧 स्टाफ', color: '#7c3aed' },
                { key: 'admin', icon: <ShieldCheck size={14} />, label: '👑 मालिक', color: 'var(--primary)' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => { setActiveTab(tab.key as typeof activeTab); if (tab.key === 'admin') { setAdminStep('credentials'); setOtpInput(''); setOtpError(''); } }}
                  className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-3 text-xs font-semibold transition-all duration-150 ${activeTab === tab.key ? 'border-b-2' : ''}`}
                  style={{
                    borderBottomColor: activeTab === tab.key ? tab.color : 'transparent',
                    color: activeTab === tab.key ? tab.color : 'var(--muted-foreground)',
                    backgroundColor: activeTab === tab.key ? `${tab.color}08` : 'transparent',
                  }}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Customer Login */}
            {activeTab === 'customer' && (
              <form onSubmit={customerForm.handleSubmit(onCustomerSubmit)} className="p-5 flex flex-col gap-4">
                <div className="text-center mb-1">
                  <div className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>👤 Customer (ग्राहक) Login</div>
                </div>
                <div>
                  <label className="hindi-label block mb-1.5">{t.mobileOrEmail}</label>
                  <input type="text" inputMode="text" placeholder={t.mobileOrEmailPlaceholder} className="input-base"
                    {...customerForm.register('identifier', {
                      required: 'मोबाइल नंबर या ईमेल जरूरी है',
                      validate: (val) => {
                        const v = val.trim();
                        return /^[6-9]\d{9}$/.test(v) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'सही 10 अंकों का मोबाइल या valid ईमेल डालें';
                      },
                    })}
                  />
                  {customerForm.formState.errors.identifier && <p className="text-xs mt-1 font-medium" style={{ color: 'var(--danger)' }}>{customerForm.formState.errors.identifier.message}</p>}
                </div>
                <div>
                  <label className="hindi-label block mb-1.5">{t.pin4digit}</label>
                  <div className="relative">
                    <input type={showPin ? 'text' : 'password'} inputMode="numeric" maxLength={4} placeholder={t.pinPlaceholder} className="input-base pr-10"
                      {...customerForm.register('pin', { required: 'PIN जरूरी है', minLength: { value: 4, message: 'PIN 4 अंकों का होना चाहिए' }, maxLength: { value: 4, message: 'PIN 4 अंकों का होना चाहिए' } })}
                    />
                    <button type="button" onClick={() => setShowPin(!showPin)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }}>
                      {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {customerForm.formState.errors.pin && <p className="text-xs mt-1 font-medium" style={{ color: 'var(--danger)' }}>{customerForm.formState.errors.pin.message}</p>}
                </div>
                <button type="submit" className="btn-primary" disabled={isLoading}>
                  {isLoading ? <span className="flex items-center justify-center gap-2"><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>{t.loggingIn}</span> : t.loginBtn}
                </button>
                <p className="text-center text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  {t.noAccount}{' '}
                  <button type="button" onClick={() => setActiveTab('signup')} className="font-semibold underline" style={{ color: '#16a34a' }}>{t.registerHere}</button>
                </p>
              </form>
            )}

            {/* Sign Up */}
            {activeTab === 'signup' && (
              <form onSubmit={signUpForm.handleSubmit(onSignUpSubmit)} className="p-5 flex flex-col gap-3.5">
                <div className="text-center mb-1">
                  <div className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>{t.createAccount}</div>
                  <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{t.createAccountSubtitle}</div>
                </div>
                {[
                  { label: t.fullName, name: 'fullName', type: 'text', placeholder: t.fullNamePlaceholder, rules: { required: 'पूरा नाम जरूरी है' } },
                  { label: t.mobileNumberSignup, name: 'phone', type: 'tel', inputMode: 'numeric', maxLength: 10, placeholder: t.mobilePlaceholder, rules: { required: 'मोबाइल नंबर जरूरी है', pattern: { value: /^[6-9]\d{9}$/, message: 'सही 10 अंकों का नंबर डालें' } } },
                  { label: t.emailId, name: 'email', type: 'email', inputMode: 'email', placeholder: t.emailPlaceholder, rules: { required: 'ईमेल आईडी जरूरी है', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'सही ईमेल आईडी डालें' } } },
                  { label: t.vehicleNumber, name: 'vehicleNumber', type: 'text', placeholder: t.bikeNumberPlaceholder, rules: { required: 'गाड़ी नंबर जरूरी है' } },
                ].map((field) => (
                  <div key={field.name}>
                    <label className="hindi-label block mb-1.5">{field.label} <span style={{ color: 'var(--danger)' }}>*</span></label>
                    <input type={field.type} inputMode={field.inputMode as React.HTMLAttributes<HTMLInputElement>['inputMode']} maxLength={field.maxLength} placeholder={field.placeholder} className="input-base"
                      {...signUpForm.register(field.name as keyof SignUpFormData, field.rules as Parameters<typeof signUpForm.register>[1])}
                    />
                    {signUpForm.formState.errors[field.name as keyof SignUpFormData] && <p className="text-xs mt-1 font-medium" style={{ color: 'var(--danger)' }}>{signUpForm.formState.errors[field.name as keyof SignUpFormData]?.message}</p>}
                  </div>
                ))}
                {[
                  { label: t.pin4digitSignup, name: 'pin', show: showSignUpPin, setShow: setShowSignUpPin, rules: { required: 'PIN जरूरी है', minLength: { value: 4, message: 'PIN 4 अंकों का होना चाहिए' }, maxLength: { value: 4, message: 'PIN 4 अंकों का होना चाहिए' }, pattern: { value: /^\d{4}$/, message: 'PIN सिर्फ 4 अंकों का होना चाहिए' } } },
                  { label: t.confirmPin, name: 'confirmPin', show: showConfirmPin, setShow: setShowConfirmPin, rules: { required: 'PIN दोबारा डालें', minLength: { value: 4, message: 'PIN 4 अंकों का होना चाहिए' } } },
                ].map((field) => (
                  <div key={field.name}>
                    <label className="hindi-label block mb-1.5">{field.label} <span style={{ color: 'var(--danger)' }}>*</span></label>
                    <div className="relative">
                      <input type={field.show ? 'text' : 'password'} inputMode="numeric" maxLength={4} placeholder={t.pinPlaceholder} className="input-base pr-10"
                        {...signUpForm.register(field.name as keyof SignUpFormData, field.rules as Parameters<typeof signUpForm.register>[1])}
                      />
                      <button type="button" onClick={() => field.setShow(!field.show)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }}>
                        {field.show ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {signUpForm.formState.errors[field.name as keyof SignUpFormData] && <p className="text-xs mt-1 font-medium" style={{ color: 'var(--danger)' }}>{signUpForm.formState.errors[field.name as keyof SignUpFormData]?.message}</p>}
                  </div>
                ))}
                <button type="submit" className="w-full py-3 rounded-lg font-bold text-sm transition-all duration-150 mt-1" style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', color: '#fff' }} disabled={isLoading}>
                  {isLoading ? <span className="flex items-center justify-center gap-2"><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>रजिस्टर हो रहा है...</span> : t.registerBtn}
                </button>
                <p className="text-center text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  {t.alreadyAccount}{' '}
                  <button type="button" onClick={() => setActiveTab('customer')} className="font-semibold underline" style={{ color: 'var(--accent)' }}>{t.loginHere}</button>
                </p>
              </form>
            )}

            {/* Staff Login */}
            {activeTab === 'staff' && (
              <form onSubmit={staffForm.handleSubmit(onStaffSubmit)} className="p-5 flex flex-col gap-4">
                <div className="rounded-lg p-3 text-sm" style={{ backgroundColor: 'rgba(124,58,237,0.07)', color: '#7c3aed' }}>
                  👨‍🔧 यह पैनल सिर्फ वर्कशॉप स्टाफ / मैकेनिक के लिए है। अपना Mobile/Email और Staff PIN डालें।
                </div>
                <div>
                  <label className="hindi-label block mb-1.5">📱 Mobile Number या Email ID</label>
                  <input type="text" inputMode="text" placeholder="10-digit Mobile या name@example.com" className="input-base" {...staffForm.register('identifier', { required: 'Mobile या Email जरूरी है' })} />
                  {staffForm.formState.errors.identifier && <p className="text-xs mt-1 font-medium" style={{ color: 'var(--danger)' }}>{staffForm.formState.errors.identifier.message}</p>}
                </div>
                <div>
                  <label className="hindi-label block mb-1.5">🔒 4-Digit Staff PIN</label>
                  <div className="relative">
                    <input type={showStaffPin ? 'text' : 'password'} inputMode="numeric" maxLength={4} placeholder="4 अंकों का Staff PIN" className="input-base pr-10"
                      {...staffForm.register('staffPin', { required: 'Staff PIN जरूरी है', minLength: { value: 4, message: 'PIN 4 अंकों का होना चाहिए' }, maxLength: { value: 4, message: 'PIN 4 अंकों का होना चाहिए' } })}
                    />
                    <button type="button" onClick={() => setShowStaffPin(!showStaffPin)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }}>
                      {showStaffPin ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {staffForm.formState.errors.staffPin && <p className="text-xs mt-1 font-medium" style={{ color: 'var(--danger)' }}>{staffForm.formState.errors.staffPin.message}</p>}
                </div>
                <button type="submit" className="w-full py-3 rounded-lg font-bold text-sm transition-all duration-150" style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)', color: '#fff' }} disabled={isLoading}>
                  {isLoading ? <span className="flex items-center justify-center gap-2"><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>वेरिफाई हो रहा है...</span> : '👨‍🔧 Staff पैनल में जाएं →'}
                </button>
                <p className="text-center text-xs" style={{ color: 'var(--muted-foreground)' }}>PIN भूल गए? मालिक (Owner) से संपर्क करें।</p>
              </form>
            )}

            {/* Admin / Owner */}
            {activeTab === 'admin' && (
              <div className="p-5 flex flex-col gap-4">
                {adminStep === 'credentials' && (
                  <form onSubmit={adminForm.handleSubmit(onAdminCredentialsSubmit)} className="flex flex-col gap-4">
                    {dailyAuthValid ? (
                      <div className="rounded-lg p-3 flex items-center gap-2.5" style={{ backgroundColor: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.25)' }}>
                        <CheckCircle size={16} style={{ color: '#16a34a', flexShrink: 0 }} />
                        <div>
                          <div className="text-xs font-bold" style={{ color: '#16a34a' }}>⚡ Daily Auth Active — Fast Login</div>
                          <div className="text-xs mt-0.5" style={{ color: '#15803d' }}>आज का OTP पहले ही verify हो चुका है। सिर्फ पासवर्ड डालें।</div>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-lg p-3 text-xs" style={{ backgroundColor: 'rgba(30,58,95,0.07)', color: 'var(--primary)' }}>
                        <div className="font-bold mb-0.5">🔐 Secure Admin Login</div>
                        <div style={{ color: 'var(--muted-foreground)' }}>पहली बार आज — Email + Password + OTP जरूरी है।</div>
                      </div>
                    )}
                    <div>
                      <label className="hindi-label block mb-1.5">📱 Mobile Number या Email ID</label>
                      <input type="text" inputMode="text" placeholder="Registered Mobile या Email" className="input-base"
                        {...adminForm.register('identifier', { required: 'Mobile या Email जरूरी है' })}
                      />
                      {adminForm.formState.errors.identifier && <p className="text-xs mt-1 font-medium" style={{ color: 'var(--danger)' }}>{adminForm.formState.errors.identifier.message}</p>}
                    </div>
                    <div>
                      <label className="hindi-label block mb-1.5">🔒 Admin पासवर्ड</label>
                      <div className="relative">
                        <input type={showAdminPassword ? 'text' : 'password'} placeholder="कम से कम 6 अंकों का पासवर्ड" className="input-base pr-10"
                          {...adminForm.register('password', { required: 'पासवर्ड जरूरी है', minLength: { value: 6, message: 'पासवर्ड कम से कम 6 अंकों का होना चाहिए।' } })}
                        />
                        <button type="button" onClick={() => setShowAdminPassword(!showAdminPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }}>
                          {showAdminPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {adminForm.formState.errors.password && <p className="text-xs mt-1 font-medium" style={{ color: 'var(--danger)' }}>{adminForm.formState.errors.password.message}</p>}
                    </div>
                    {!deviceTrusted && (
                      <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-lg p-3" style={{ backgroundColor: 'rgba(249,115,22,0.04)', border: '1px solid rgba(249,115,22,0.15)' }}>
                        <div className="relative mt-0.5 shrink-0">
                          <input type="checkbox" checked={trustCheckbox} onChange={(e) => setTrustCheckbox(e.target.checked)} className="sr-only" />
                          <div className="w-4 h-4 rounded flex items-center justify-center transition-all" style={{ backgroundColor: trustCheckbox ? 'var(--accent)' : 'transparent', border: `2px solid ${trustCheckbox ? 'var(--accent)' : 'var(--border)'}` }}>
                            {trustCheckbox && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-bold flex items-center gap-1" style={{ color: 'var(--foreground)' }}><Smartphone size={12} style={{ color: 'var(--accent)' }} />Trust this device for 30 days</div>
                          <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>यह मेरा निजी फोन है — अगले 30 दिन सिर्फ पासवर्ड से login</div>
                        </div>
                      </label>
                    )}
                    {deviceTrusted && (
                      <div className="rounded-lg p-2.5 flex items-center gap-2" style={{ backgroundColor: 'rgba(22,163,74,0.06)', border: '1px solid rgba(22,163,74,0.2)' }}>
                        <CheckCircle size={14} style={{ color: '#16a34a' }} />
                        <span className="text-xs font-medium" style={{ color: '#16a34a' }}>Trusted Device — 30 दिन active</span>
                        <button type="button" onClick={() => { clearDeviceTrust(); setDeviceTrusted(false); }} className="ml-auto text-xs underline" style={{ color: '#dc2626' }}>हटाएं</button>
                      </div>
                    )}
                    <button type="submit" className="w-full py-3 rounded-lg font-bold text-sm transition-all duration-150"
                      style={{ background: dailyAuthValid ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' : 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)', color: '#fff' }}
                      disabled={isLoading}
                    >
                      {isLoading ? <span className="flex items-center justify-center gap-2"><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>वेरिफाई हो रहा है...</span> : dailyAuthValid ? '⚡ Login करें →' : '🔐 आगे बढ़ें (OTP के लिए) →'}
                    </button>
                    <p className="text-center text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      पहली बार?{' '}
                      <button type="button" onClick={() => router.push('/set-admin-password')} className="font-semibold underline" style={{ color: 'var(--accent)' }}>Admin पासवर्ड सेट करें</button>
                    </p>
                  </form>
                )}

                {adminStep === 'otp' && (
                  <div className="flex flex-col gap-4">
                    <div className="rounded-lg p-3 text-xs" style={{ backgroundColor: 'rgba(249,115,22,0.07)', border: '1px solid rgba(249,115,22,0.2)' }}>
                      <div className="font-bold mb-1" style={{ color: 'var(--accent)' }}>📧 Email OTP Verification</div>
                      <div style={{ color: 'var(--muted-foreground)' }}>OTP भेजा गया: <span className="font-semibold" style={{ color: 'var(--foreground)' }}>ramhitg1703@gmail.com</span></div>
                      <div className="mt-1" style={{ color: 'var(--muted-foreground)' }}>OTP ऊपर Toast notification में भी दिखाया गया है।</div>
                    </div>

                    {/* ── On-screen OTP fallback — always visible ── */}
                    {generatedOtp && (
                      <div className="rounded-xl p-4 text-center" style={{ backgroundColor: 'rgba(30,58,95,0.08)', border: '2px solid rgba(30,58,95,0.25)' }}>
                        <div className="text-xs font-semibold mb-2" style={{ color: 'var(--muted-foreground)' }}>🔐 आपका OTP (Screen पर)</div>
                        <div className="text-4xl font-black tracking-[0.3em]" style={{ color: 'var(--primary)', fontVariantNumeric: 'tabular-nums' }}>{generatedOtp}</div>
                        <div className="text-xs mt-2" style={{ color: 'var(--muted-foreground)' }}>Email न आए तो यह OTP use करें</div>
                      </div>
                    )}

                    <div>
                      <label className="hindi-label block mb-1.5">🔢 6-Digit OTP डालें</label>
                      <input type="text" inputMode="numeric" maxLength={6} placeholder="6 अंकों का OTP" className="input-base text-center text-lg font-bold tracking-widest"
                        value={otpInput} onChange={(e) => { setOtpInput(e.target.value.replace(/\D/g, '')); setOtpError(''); }} autoFocus
                      />
                      {otpError && <p className="text-xs mt-1 font-medium" style={{ color: 'var(--danger)' }}>{otpError}</p>}
                    </div>
                    <button type="button" onClick={handleOtpVerify} disabled={isLoading || otpInput.length < 6}
                      className="w-full py-3 rounded-lg font-bold text-sm transition-all duration-150"
                      style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)', color: '#fff', opacity: otpInput.length < 6 ? 0.6 : 1 }}
                    >
                      {isLoading ? <span className="flex items-center justify-center gap-2"><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>वेरिफाई हो रहा है...</span> : '✅ OTP Verify करें →'}
                    </button>
                    <button type="button" onClick={() => { setAdminStep('credentials'); setOtpInput(''); setOtpError(''); }}
                      className="w-full py-2 rounded-lg text-sm font-medium transition-all"
                      style={{ color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}
                    >
                      ← वापस जाएं
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}